import React, { useCallback, useEffect, useMemo, useState } from "react";
import { QuestionsIF } from "./QuestionsIF";
import { buildAgeFeature } from "fe-common/patientCase/AgeAndGenderQFeatures";
import { MeasurementUnits } from "fe-common/graphql";
import { DocQuestion } from "./DocQuestion";
import { styled } from "@mui/material";
import { ContinueButton } from "../uiControllers/continueButton";
import { useTranslate } from "../../hooks/useTranslate";
import { DisclaimerText } from "../uiControllers/disclaimerText";
import { PatientTextField } from "../uiControllers/patientTextField";
import { useInputStyles } from "../../style/input";

const AgeQuestionView = styled("div")({
  display: "flex",
  width: "100%",
  flexDirection: "column",
});

const AgeQuestionTopContainer = styled("div")({
  display: "flex",
  width: "fit-content",
});

const AgeQuestionContainer = styled("div")({
  display: "flex",
  width: "100%",
  marginTop: "10px",
  flexDirection: "column",
  alignItems: "flex-end",
});

const AgeInput = styled(PatientTextField)({});

const MAX_AGE = 120;
const MIN_AGE = 16;

export const AgeQuestion = ({ answer, onAnswer, ...props }: QuestionsIF) => {
  const [age, setAge] = useState<string>("");

  const classes = useInputStyles();

  const { translate } = useTranslate();

  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    if (!answer) {
      setAge(null);
    }
  }, [setAge, answer]);

  const onAgeChange = (event) => {
    const value = event.target.value;

    setShowDisclaimer(false);

    if (
      value !== "" &&
      !(value.charAt(value.length - 1) === ".") &&
      !(value.charAt(value.length - 1) === "0") &&
      !Number(value.charAt(value.length - 1))
    ) {
      return;
    }

    setAge(value || null);
  };

  const isValidMaxAge = useMemo(() => {
    const ageNumber = parseInt(age);
    return ageNumber <= MAX_AGE;
  }, [age]);

  const isValidMinAge = useMemo(() => {
    const ageNumber = parseInt(age);
    return ageNumber >= MIN_AGE;
  }, [age]);

  const isValidAge = useMemo(() => {
    return isValidMaxAge && isValidMinAge;
  }, [isValidMinAge, isValidMaxAge]);

  const onDone = useCallback(() => {
    if (!isValidAge) {
      setShowDisclaimer(true);
      return;
    }

    onAnswer([
      buildAgeFeature({
        low: parseInt(age),
        high: parseInt(age),
        units: MeasurementUnits.Years,
        complexUnits: null,
      }),
    ]);
  }, [age, onAnswer, isValidAge]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && isValidAge) {
        onDone();
      }
    },
    [isValidAge, onDone],
  );

  return (
    <AgeQuestionView>
      <AgeQuestionTopContainer className={"age-question-top-container"}>
        <DocQuestion
          text={translate("QUESTION_AGE")}
          miniView={props.miniView}
        />
      </AgeQuestionTopContainer>
      {!answer && (
        <AgeQuestionContainer>
          <AgeInput
            onKeyDown={handleKeyDown}
            value={age || ""}
            inputProps={{ pattern: "[0-9]*", inputMode: "numeric" }}
            InputProps={{
              classes: {
                root: classes.input,
              },
            }}
            onBlur={onAgeChange}
            onChange={onAgeChange}
            variant="outlined"
            style={{ width: "100%" }}
            id="outlined-basic"
          />

          {age && <ContinueButton onClick={onDone} />}

          {age && !isValidMaxAge && showDisclaimer && (
            <DisclaimerText disclaimerText={translate("MAX_AGE_ERROR")} />
          )}
          {age && !isValidMinAge && showDisclaimer && (
            <DisclaimerText disclaimerText={translate("MIN_AGE_ERROR")} />
          )}
        </AgeQuestionContainer>
      )}
    </AgeQuestionView>
  );
};
